import React, { useRef, useState, useEffect } from 'react'
import { FaPlus, FaCopy, FaTrash, FaExclamationTriangle, FaCheck, FaVideo } from 'react-icons/fa'
import { GrDrag } from 'react-icons/gr'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Box, Stack, Wrap, WrapItem, Heading, Tag, Text, FormControl, Input, Textarea, Select, SearchableSelect, Checkbox, Icon, Button, Alert, Tooltip, Badge, ConfirmButton, RichText, SimpleModal, FileChooser, VideoViewer, DateChooser, TimeChooser, SimpleTabs, BottomStickyBar } from '@/components/lib'
import utils, { ratingPoints } from '@/utils/utils'
import api from '@/api'
import useStore from '@/store'
import DataTypeEditor from '../NewData'
import DataTypeConfiguration, { useDataTypeConfiguration } from '../DataTypeConfiguration'
import ResponseEditor from '../Participant/ResponseEditor'

export default function Editor ({ trial, stage, name, setName, description, setDescription, components, setComponents, dataTypes, postSubmissionText, setPostSubmissionText, allowIncompleteSubmission, setAllowIncompleteSubmission, oneQuestionPerScreen, setOneQuestionPerScreen, showProgressBar, setShowProgressBar, showPostSubmissionEditor, setShowPostSubmissionEditor, errorComponent }) {
  const [selectedComponent, setSelectedComponent] = useState()
  const [previewIsOpen, setPreviewIsOpen] = useState(false)
  const [dataTypePickerIsOpen, setDataTypePickerIsOpen] = useState(false)
  const componentListRef = useRef(null)

  // When error component changes, scroll the view to that component
  useEffect(() => {
    if (errorComponent && componentListRef.current) {
      const errorElement = componentListRef.current.querySelector(`[data-component-id="${errorComponent}"]`)
      const container = componentListRef.current

      // Calculate the position relative to the container
      const containerRect = container.getBoundingClientRect()
      const elementRect = errorElement.getBoundingClientRect()

      // Calculate scroll position to center the element
      const scrollTop = container.scrollTop +
        (elementRect.top - containerRect.top) -
        (container.clientHeight / 2) +
        (errorElement.offsetHeight / 2)

      setTimeout(() => {
        container.scrollIntoView({ behavior: 'smooth' })
        container.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        })
      }, 500)
    }
  }, [errorComponent])

  // `createdDataType` is passed when the data type was made from the picker just
  // now, so the field can be titled after it before the store has caught up
  function addComponent (beforeIndex, dataTypeId, type, copyFrom, createdDataType) {
    const dataType = createdDataType ?? dataTypes?.find(d => d._id === dataTypeId)
    let title = 'Untitled field'
    if (type === 'title') title = 'Untitled title'
    else if (type === 'sectionDivider') title = 'Untitled section divider'
    else if (type === 'scheduledNotification') title = 'Untitled scheduled notification'
    else if (dataType) title = `${dataType.name}`
    const newComponents = Object.assign([], components)
    const newComponent = copyFrom
      ? { ...copyFrom, id: utils.uuidv4(), title: `Copy of ${copyFrom?.title || 'New field'}` }
      : {
          id: utils.uuidv4(),
          title,
          description: '',
          dataType: dataTypeId ?? '',
          type: type ?? undefined // For handling built-in (title, etc.)
        }
    if (beforeIndex === null || beforeIndex === undefined) newComponents.push(newComponent)
    else newComponents.splice(beforeIndex, 0, newComponent)
    setComponents(newComponents)
    return newComponent
  }
  function deleteComponent (componentId) {
    const newComponents = Object.assign([], components)
    const index = components.map(c => c.id).indexOf(componentId)
    newComponents.splice(index, 1)
    setComponents(newComponents)
  }
  function updateEntireComponent (componentId, data) {
    const newComponents = Object.assign([], components).map(c => {
      let component = c
      if (component.id === componentId) {
        component = Object.assign({}, c, data)
      }
      return component
    })
    setComponents(newComponents)
  }
  function moveComponent (componentId, newIndex) {
    const newComponents = Object.assign([], components.filter(c => c.id !== componentId))
    const component = Object.assign({}, components.filter(c => c.id === componentId)[0])
    newComponents.splice(newIndex, 0, component)
    setComponents(newComponents)
  }

  return (
    <Stack gap={5}>
      <Stack gap={3} p={3} borderWidth='1px' rounded='md' bg='bg.subtle'>
        <Box>
          <Heading as='h3' size='md'>About your stage</Heading>
          <Text color='fg.muted'>This information is used by trial staff to understand the the purpose of this stage. In ePRO settings, the information will also be available to participants.</Text>
        </Box>

        <FormControl
          label='Name'
          helperText='Give your stage a useful name to help identify it.'
        >
          <Input bg='bg.surface' placeholder='Stage 1' value={name ?? ''} onChange={e => setName(e.target.value)} />
        </FormControl>

        <FormControl
          label='Description'
          helperText='Provide some additional information about this stage, along with any general instructions.'
        >
          <Textarea bg='bg.surface' placeholder='The following questions will...' value={description ?? ''} onChange={e => setDescription(e.target.value)} />
        </FormControl>
      </Stack>

      <Stack gap={3} p={3} borderWidth='1px' rounded='md' bg='bg.subtle'>
        <Box display='flex' justifyContent='space-between'>
          <Box>
            <Heading as='h3' size='md'>Form builder</Heading>
            <Text color='fg.muted'>Set-up the questions that form this stage.</Text>
          </Box>
          <Box>
            {components?.length > 0 && <Button size='sm' colorPalette='blue' variant='outline' onClick={e => setPreviewIsOpen(true)}>Preview form (CRF view)</Button>}
          </Box>
        </Box>

        <Box p={2} rounded='md' bg='bg.muted' boxShadow='inner' maxH='700px' overflowY='auto' ref={componentListRef}>
          {components?.length === 0 &&
            <Box textAlign='center' mt={5} mb={5}>
              <Heading size='md'>Your form doesn't have any fields yet</Heading>
              <Button size='sm' colorPalette='primary' mt={5} onClick={e => setDataTypePickerIsOpen(true)}>Add a field</Button>

              <DataTypePicker
                isOpen={dataTypePickerIsOpen} onClose={() => setDataTypePickerIsOpen(false)} onSelect={(dt, t, openEditor, createdDataType) => {
                  const added = addComponent(0, dt, t, null, createdDataType)
                  setDataTypePickerIsOpen(false)
                  if (openEditor) setSelectedComponent(added)
                }} trial={trial} dataTypes={dataTypes} usedDataTypeIds={components.map(c => c.dataType)}
              />
            </Box>}

          <DndProvider backend={HTML5Backend}>
            {components?.map((component, i) =>
              <ComponentItem key={component.id} trial={trial} component={component} index={i} components={components} dataTypes={dataTypes} moveComponent={moveComponent} setSelectedComponent={setSelectedComponent} deleteComponent={deleteComponent} addComponent={addComponent} hasError={errorComponent === component.id} />
            )}
          </DndProvider>
        </Box>
      </Stack>

      {selectedComponent &&
        <ComponentEditor component={selectedComponent} updateComponent={updateEntireComponent} dataTypes={dataTypes} trial={trial} onClose={() => setSelectedComponent(null)} />}

      <ResponseEditor
        isOpen={previewIsOpen} previewComponents={
        components.map(c => {
          const cDataType = dataTypes?.find(d => d._id === c.dataType)
          return { ...c, type: cDataType || c.type }
        })
} onClose={() => setPreviewIsOpen(false)}
      />

      {showPostSubmissionEditor && // Delay until stage is loaded
        <Stack gap={3} p={3} borderWidth='1px' rounded='md' bg='bg.subtle'>
          <Heading as='h3' size='md'>Post-submission text</Heading>
          <Text color='fg.muted'>In ePRO settings, this text will be displayed to participants after they complete this stage.</Text>
          <RichText value={postSubmissionText ?? ''} onChange={e => setPostSubmissionText(e)} forType='trial' forId={trial?._id} allowImages />
        </Stack>}

      <Stack gap={3} p={3} borderWidth='1px' rounded='md' bg='bg.subtle'>
        <Heading as='h3' size='md'>Submission rules</Heading>
        <Text color='fg.muted'>
          Allow this stage to be submitted even when required fields have not been
          completed. Individual field checks (e.g. valid choice, number ranges) still
          apply.
        </Text>
        <FormControl>
          <Checkbox
            isChecked={allowIncompleteSubmission}
            onChange={e => setAllowIncompleteSubmission(e.checked)}
          >
            Allow partial submission (skip required-field checks)
          </Checkbox>
        </FormControl>
      </Stack>

      <Stack gap={3} p={3} borderWidth='1px' rounded='md' bg='bg.subtle'>
        <Heading as='h3' size='md'>Display</Heading>
        <Text color='fg.muted'>
          Show participants one question at a time, with Next and Back buttons,
          instead of a single scrolling form. Headings and section dividers are
          shown above the question that follows them. This applies in the
          participant app and on questionnaire links; it does not change how
          staff enter responses on a participant's behalf.
        </Text>
        <FormControl>
          <Checkbox
            isChecked={oneQuestionPerScreen}
            onChange={e => setOneQuestionPerScreen(e.checked)}
          >
            Show one question per screen
          </Checkbox>
        </FormControl>
        {oneQuestionPerScreen &&
          <FormControl>
            <Checkbox
              isChecked={showProgressBar}
              onChange={e => setShowProgressBar(e.checked)}
            >
              Show a progress bar
            </Checkbox>
            <Text color='fg.muted' fontSize='sm'>
              A bar along the top of the stage showing how far through the
              questions the participant is.
            </Text>
          </FormControl>}
      </Stack>
    </Stack>
  )
}

function DataTypePicker ({ isOpen, onClose, onSelect, trial, dataTypes, usedDataTypeIds }) {
  const [filter, setFilter] = useState('')
  const { dataTypeTags } = useStore()

  const availableDataTypes = dataTypes?.filter(d => usedDataTypeIds.indexOf(d._id) === -1)
  const filterMatchedTagIds = dataTypeTags?.filter(t => t.name.toLowerCase().indexOf(filter.toLowerCase()) > -1)?.map(t => t._id)
  const filteredAvailableDataTypes = filter
    ? availableDataTypes?.filter(d =>
      d.name.toLowerCase().includes(filter) ||
    d.nickname?.toLowerCase().includes(filter) ||
    d.tags?.some(t => filterMatchedTagIds.indexOf(t) > -1)
    )
    : availableDataTypes

  return (
    <SimpleModal
      isOpen={isOpen} onClose={onClose} size='xl'
      header='Pick a data type'
      body={
        <Stack direction='row' spacing={2} alignItems='start'>
          <Box bg='bg.subtle' p={2} rounded='md' flex={1}>
            <Text mb={3} color='gray' fontWeight='bold' textTransform='uppercase' fontSize='xs'>Data types</Text>
            {dataTypes?.length > 0 && availableDataTypes?.length === 0 &&
              <Alert variant='left-accent'>You have no more data types to collect.</Alert>}
            {dataTypes?.length === 0 &&
              <Alert fontSize='sm'>You don't have any datatypes yet</Alert>}
            {availableDataTypes?.length > 0 &&
              <>
                <Input bg='bg.surface' mb={2} size='xs' placeholder='Search by name, nickname or tag...' value={filter ?? ''} onChange={e => setFilter(e.target.value.toLowerCase())} />
                <Box overflowY='auto' maxH={350} mt={2} mb={2}>
                  <Wrap gap={2}>
                    {filteredAvailableDataTypes?.map(d =>
                      <WrapItem key={d._id}>
                        <Box cursor='pointer' bg='bg.surface' rounded='md' p={2} fontSize='sm' _hover={{ bg: 'bg.subtle' }} onClick={e => onSelect(d._id)}>
                          {d.name}
                        </Box>
                      </WrapItem>
                    )}
                  </Wrap>
                </Box>
              </>}
            <DataTypeEditor
              trial={trial}
              onComplete={dataType => onSelect(dataType._id, null, true, dataType)}
              trigger={
                <Button size='sm' width='100%' mt={2}><Icon as={FaPlus} /> Create a new data type (variable)</Button>
              }
            />
          </Box>

          <Box bg='bg.subtle' p={2} rounded='md' maxW='150px'>
            <Text mb={3} color='gray' fontWeight='bold' textTransform='uppercase' fontSize='xs'>Other fields</Text>
            <Stack spacing={2}>
              <Box fontSize='sm' bg='bg.surface' p={2} rounded='md' cursor='pointer' onClick={e => onSelect(null, 'title')} _hover={{ bg: 'bg.subtle' }}>Title area</Box>
              <Box fontSize='sm' bg='bg.surface' p={2} rounded='md' cursor='pointer' onClick={e => onSelect(null, 'sectionDivider')} _hover={{ bg: 'bg.subtle' }}>Section divider</Box>
              <Box fontSize='sm' bg='bg.surface' p={2} rounded='md' cursor='pointer' onClick={e => onSelect(null, 'scheduledNotification')} _hover={{ bg: 'bg.subtle' }}>Scheduled notification</Box>
            </Stack>
          </Box>
        </Stack>
      }
    />
  )
}

function ComponentItem ({ trial, component, dataTypes, components, index, moveComponent, setSelectedComponent, deleteComponent, addComponent, hasError }) {
  const [dataTypePickerIsOpen, setDataTypePickerIsOpen] = useState(false)
  const [insertIndex, setInsertIndex] = useState(null)
  const [isHoveringGrab, setIsHoveringGrab] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const ref = useRef(null)
  const [, drop] = useDrop({
    accept: 'COMPONENT',
    hover (item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return
      if (!item.typeId && !item.type) {
        moveComponent(component.id, dragIndex)
      }
      item.index = hoverIndex
    }
  })
  const [{ isDragging }, drag] = useDrag({
    type: 'COMPONENT',
    item: () => {
      return { id: component.id, index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })
  const opacity = isDragging ? 0 : 1
  drag(drop(ref))

  const clashedDataType = components?.filter(c => c.id !== component.id).map(c => c.dataType).indexOf(component.dataType) > -1
  const dataType = dataTypes?.find(d => d._id === component.dataType)
  const conditionDataType = dataTypes?.find(d => d._id === component.condition?.dataType)
  return (
    <Box onMouseEnter={e => setIsHovering(true)} onMouseLeave={e => setIsHovering(false)}>
      <Box style={{ opacity: isHovering ? 1 : 0 }}>
        <Box p={2} cursor='pointer' onClick={e => { setDataTypePickerIsOpen(true); setInsertIndex(index) }} fontSize='xs' textAlign='center'><Icon as={FaPlus} /> Add a field</Box>
        <DataTypePicker
          isOpen={dataTypePickerIsOpen} onClose={() => setDataTypePickerIsOpen(false)} onSelect={(dt, t, openEditor, createdDataType) => {
            const added = addComponent(insertIndex, dt, t, null, createdDataType)
            setDataTypePickerIsOpen(false)
            if (openEditor) setSelectedComponent(added)
          }} trial={trial} dataTypes={dataTypes} usedDataTypeIds={components.map(c => c.dataType)}
        />
      </Box>
      <Box
        ref={ref} data-component-id={component.id} style={{ opacity }}
        bg={hasError ? 'red.200' : (component.type ? null : 'bg.surface')}
        _dark={{ bg: hasError ? 'red.900' : undefined }}
        p={3} position='relative' display='flex' justifyContent='space-between' alignItems='center'
        transition='0.25s' shadow={isHoveringGrab ? 'xl' : null}
        _hover={{ bg: component.type ? 'bg.muted' : 'bg.subtle' }}
        rounded='md'
      >
        <Box w={30} display='flex' alignItems='center'>
          <Icon as={GrDrag} w={5} h={5} cursor='grab' onMouseEnter={e => setIsHoveringGrab(true)} onMouseLeave={e => setIsHoveringGrab(false)} />
        </Box>

        {component.type
          ? (
            <Stack flex={1} cursor='pointer' onClick={() => setSelectedComponent(component)}>
              <Stack direction='row' alignItems='center'>
                {component.type === 'title' && <Tag size='sm' colorPalette='blue'>Title</Tag>}
                {component.type === 'sectionDivider' && <Tag size='sm' colorPalette='blue'>Section divider</Tag>}
                {component.type === 'scheduledNotification' && <Tag size='sm' colorPalette='blue'>Scheduled notification</Tag>}
                {conditionDataType && <Badge size='xs' colorPalette='orange' fontSize={9}>Depends on {conditionDataType.name}</Badge>}
              </Stack>
              <Heading size='sm'>{component.title || 'Untitled'}</Heading>
            </Stack>)
          : (
            <Stack flex={1} cursor='pointer' onClick={() => setSelectedComponent(component)}>
              <Stack direction='row' alignItems='center'>
                {dataType && <Tag size='sm' colorPalette='blue'>{dataType.name} ({dataType.type})</Tag>}
                {component.isRequired && <Badge colorPalette='red' fontSize={9}>Required</Badge>}
                {conditionDataType && <Badge size='xs' colorPalette='orange' fontSize={9}>Depends on {conditionDataType.name}</Badge>}
                {clashedDataType && <Tooltip label='The chosen datatype is being used multiple times in this stage'><Icon as={FaExclamationTriangle} color='red.400' /></Tooltip>}
                {(!dataType || !component.title) && <Tooltip label='This component is not completed'><Icon as={FaExclamationTriangle} color='orange.400' /></Tooltip>}
              </Stack>
              <Stack direction='row'>
                <Heading size='sm'>{component.title || 'Untitled question'}</Heading>
              </Stack>
            </Stack>)}

        <Box>
          <Button size='xs' onClick={() => addComponent(index + 1, null, null, component)}><Icon as={FaCopy} /></Button>
          <ConfirmButton
            trigger={<Button size='xs' ml={2} colorPalette='gray' variant='outline'><Icon as={FaTrash} /></Button>}
            header='Really delete this component?'
            footer='Any data collected so far for this component may be lost.'
            onConfirm={e => deleteComponent(component.id)}
            actionColor='red'
          />
        </Box>
      </Box>

      {index === components.length - 1 &&
        <Box style={{ opacity: isHovering ? 1 : 0 }}>
          <Box p={2} cursor='pointer' onClick={e => { setDataTypePickerIsOpen(true); setInsertIndex(index + 1) }} fontSize='xs' textAlign='center'><Icon as={FaPlus} /> Add a field</Box>
        </Box>}
    </Box>
  )
}

// Built-in components a condition can be set on, which is all of them: a title
// or section divider introduces the fields below it, and a scheduled
// notification prompts the participant about them, so all three make sense to
// hide along with the fields they belong to. Mirrors
// CONDITIONAL_BUILT_IN_TYPES in api/api/stages.py.
const CONDITIONAL_BUILT_IN_TYPES = ['title', 'sectionDivider', 'scheduledNotification']

export const supportsConditions = component => !component.type || CONDITIONAL_BUILT_IN_TYPES.indexOf(component.type) > -1

// Marks the data type settings below as belonging to no field yet, so opening a
// second field that happens to use the same data type still reloads them
const NOTHING_LOADED = Symbol('nothing loaded')

function ComponentEditor ({ component, dataTypes, trial, updateComponent, onClose }) {
  const [isOpen, setIsOpen] = useState(false)
  const [changesMade, setChangesMade] = useState(false)
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
  // RichText fires onChange once on mount to normalise its value (e.g. null → '<p></p>').
  // We capture that first call as a baseline so we don't treat it as a user edit.
  // Reset to null each time a new component is opened so the baseline is re-captured.
  const descriptionBaselineRef = useRef(null)
  // Which data type's settings are currently in the editor below
  const loadedDataTypeRef = useRef(NOTHING_LOADED)
  const [componentDataType, setComponentDataType] = useState()
  const [componentIsRequired, setComponentIsRequired] = useState(false)
  const [componentTitle, setComponentTitle] = useState('')
  const [componentDescription, setComponentDescription] = useState('')
  const [componentNotificationTitle, setComponentNotificationTitle] = useState('')
  const [componentNotificationBody, setComponentNotificationBody] = useState('')
  const [componentNotificationDelay, setComponentNotificationDelay] = useState(0)
  const [componentVideo, setComponentVideo] = useState(null)
  const [conditional, setConditional] = useState(false)
  const [conditionDataType, setConditionDataType] = useState(null)
  const [conditionOperator, setConditionOperator] = useState('equal')
  const [conditionValue, setConditionValue] = useState('')
  // Likert only: which statement to branch on. '' means the total across them all.
  const [conditionStatement, setConditionStatement] = useState('')
  const [scoringRules, setScoringRules] = useState([])
  // The settings of the data type itself, edited in place so a stage can be
  // built without a trip to the data types screen. They belong to the data
  // type rather than to this field, so they are saved separately.
  const dataTypeConfig = useDataTypeConfiguration()
  const [dataTypeConfigChanged, setDataTypeConfigChanged] = useState(false)
  const [dataTypeError, setDataTypeError] = useState(null)
  const [savingDataType, setSavingDataType] = useState(false)
  const { addDataType } = useStore()

  useEffect(() => {
    if (component) {
      setComponentDataType(component.dataType)
      setComponentIsRequired(component.isRequired)
      setComponentTitle(component.title)
      setComponentDescription(component.description || '')
      setComponentNotificationTitle(component.notificationTitle)
      setComponentNotificationBody(component.notificationBody)
      setComponentNotificationDelay(component.notificationDelay)
      setComponentVideo(component.video)
      setConditional(!!component.condition?.dataType)
      setConditionDataType(component.condition?.dataType)
      setConditionOperator(component.condition?.operator)
      setConditionValue(component.condition?.value)
      setConditionStatement(component.condition?.statement || '')
      setScoringRules(component.scoringRules || [])
    }
    setIsOpen(!!component)
    setChangesMade(false)
    descriptionBaselineRef.current = null
    loadedDataTypeRef.current = NOTHING_LOADED
    loadDataTypeConfig(component?.dataType)
  }, [component])

  // Follow the picker onto a different data type. Loading once per id — rather
  // than whenever the list changes — keeps saving the data type from wiping an
  // in-progress edit, while still waiting for a newly created one to arrive.
  useEffect(() => {
    // undefined only before the effect above has run, and resetting on that
    // first pass would flash the defaults over the settings it just loaded
    if (componentDataType === undefined) return
    loadDataTypeConfig(componentDataType)
  }, [componentDataType, dataTypes])

  // Pull a data type's own settings into the editor below the picker
  function loadDataTypeConfig (dataTypeId) {
    if (loadedDataTypeRef.current === dataTypeId) return
    const dataType = dataTypes?.find(d => d._id === dataTypeId)
    // A data type just created from in here hasn't reached the list yet
    if (dataTypeId && !dataType) return
    loadedDataTypeRef.current = dataTypeId
    if (dataType) dataTypeConfig.load(dataType)
    else dataTypeConfig.reset()
    setDataTypeConfigChanged(false)
    setDataTypeError(null)
  }

  // Detect changes to fields that use standard inputs (which don't fire onChange on mount).
  // Description is intentionally excluded — RichText fires onChange on mount to normalise its
  // value, so description changes are tracked separately via descriptionBaselineRef.
  useEffect(() => {
    if (isOpen && (component.dataType !== componentDataType ||
      component.isRequired !== componentIsRequired ||
      component.title !== componentTitle ||
      component.video !== componentVideo ||
      component.condition?.dataType !== conditionDataType ||
      component.condition?.operator !== conditionOperator ||
      component.condition?.value !== conditionValue ||
      (component.condition?.statement || '') !== conditionStatement
    )) {
      setChangesMade(true)
    }
  }, [componentDataType, componentIsRequired, componentTitle, componentVideo, conditionDataType, conditionOperator, conditionValue, conditionStatement])

  // Setters that only drive the preview or a half-typed entry, so touching them
  // shouldn't count as changing the data type
  const localOnlySetters = [
    'setLikertPreview', 'setTablePreview', 'setNewChoiceOption', 'setChoiceOptionError',
    'setNewStroopOption', 'setNewStroopColour', 'setSelectedDataTypeForInsert',
    'setGeneratingDescription', 'setFormulaDescription', 'setRatingPreview'
  ]
  // Editing any real setting marks the data type dirty, so an untouched field
  // is saved without writing to the data type at all
  const trackedDataTypeConfig = Object.fromEntries(Object.entries(dataTypeConfig).map(([key, value]) => {
    if (typeof value !== 'function' || !key.startsWith('set') || localOnlySetters.indexOf(key) > -1) return [key, value]
    return [key, (...args) => {
      setDataTypeConfigChanged(true)
      setChangesMade(true)
      value(...args)
    }]
  }))

  // Persist the data type's own settings, then hand the saved document back.
  // The Save button sits outside the tabs, so a failure is toasted as well as
  // shown inline — the inline alert is on a tab the admin may not be looking at.
  const saveDataTypeSettings = onSaved => {
    const dataType = dataTypes?.find(d => d._id === componentDataType)
    if (!dataType) return onSaved(null)
    const failed = message => {
      setDataTypeError(message)
      utils.toast('error', 'Unable to save this data type', message)
    }
    const { data, error } = dataTypeConfig.build(dataType.type)
    if (error) return failed(error)
    setDataTypeError(null)
    setSavingDataType(true)
    api.dataTypes.update(dataType._id, data, saved => {
      setSavingDataType(false)
      setDataTypeConfigChanged(false)
      addDataType(saved)
      utils.toast('success', 'Data type saved')
      onSaved(saved)
    }, err => {
      setSavingDataType(false)
      failed(err.message)
    })
  }

  const keepChanges = () => {
    // The data type is shared, so its settings are saved now rather than left
    // to the stage's own save
    if (dataTypeConfigChanged && dataTypes?.some(d => d._id === componentDataType)) {
      return saveDataTypeSettings(saved => { if (saved) applyChanges() })
    }
    applyChanges()
  }

  const applyChanges = () => {
    const newUpdate = { dataType: componentDataType, isRequired: componentIsRequired, title: componentTitle, description: componentDescription, notificationTitle: componentNotificationTitle, notificationBody: componentNotificationBody, notificationDelay: componentNotificationDelay, video: componentVideo, condition: null, scoringRules }
    if (conditional) {
      let val = conditionValue
      const conditionDataTypeObject = dataTypes?.find(d => d._id === conditionDataType)
      if (['float', 'height', 'weight', 'temperature', 'bmi'].indexOf(conditionDataTypeObject?.type) > -1) val = parseFloat(conditionValue)
      if (['integer', 'slider', 'rating', 'likert'].indexOf(conditionDataTypeObject?.type) > -1) val = parseInt(conditionValue)
      newUpdate.condition = {
        dataType: conditionDataType,
        operator: conditionOperator,
        value: val
      }
      // Only meaningful for a Likert, and only when a statement was picked —
      // left off, the condition branches on the total across all statements
      if (conditionDataTypeObject?.type === 'likert' && conditionStatement) {
        newUpdate.condition.statement = conditionStatement
      }
    }
    updateComponent(component.id, newUpdate)
    setIsOpen(false)
  }

  const checkOnClose = () => {
    if (changesMade) {
      setConfirmCloseOpen(true)
      return
    }
    setIsOpen(false)
    onClose()
  }

  const forceClose = () => {
    setConfirmCloseOpen(false)
    setIsOpen(false)
    onClose()
  }

  // "field" reads wrong on a built-in component, which collects nothing
  const COMPONENT_NOUNS = { title: 'title', sectionDivider: 'section', scheduledNotification: 'notification' }
  const componentNoun = COMPONENT_NOUNS[component.type] ?? 'field'
  const conditionDataTypeObject = dataTypes.find(d => d._id === conditionDataType)
  const componentDataTypeObject = dataTypes.find(d => d._id === componentDataType)
  const VideoElement = (
    <FormControl label='Guidance or informational video'>
      {!componentVideo?.url &&
        <FileChooser
          accept='video/*,.mp4,.mkv,.avi,.mov'
          forType='trial' forObject={trial}
          onComplete={({ storedName, name, expectedUrl }) => setComponentVideo({ name, fileName: storedName, needsVideoProcess: true, url: expectedUrl })}
          trigger={<Button size='sm' variant='outline' colorPalette='primary'><Box as={FaVideo} mr={2} /> Choose a file</Button>}
        />}
      {componentVideo?.url &&
        <VideoViewer url={componentVideo.url} onRemove={() => setComponentVideo(null)} isProcessing={componentVideo.needsVideoProcess} hasError={componentVideo.processError} />}
    </FormControl>
  )
  return (
    <>
      <SimpleModal
        isOpen={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
        header='Discard changes?'
        body='You have unsaved changes to this field. If you close now, they will be lost.'
        footer={
          <>
            <Button variant='ghost' onClick={() => setConfirmCloseOpen(false)}>Keep editing</Button>
            <Button colorPalette='red' onClick={forceClose}>Discard changes</Button>
          </>
      }
      />
      <SimpleModal
        isOpen={isOpen} onClose={checkOnClose} size='xl'
        header='Edit field'
        body={
          <Box minH='500px' position='relative'>

            <SimpleTabs
              colorPalette='gray'
              defaultValue='essentials'
              variant='line'
              tabs={[
                {
                  key: 'essentials',
                  title: 'Essentials',
                  content: (
                    <Stack w='100%' gap={3}>
                      <Box>
                        <Heading size='md'>Basic settings</Heading>
                        <Text fontSize='sm' color='fg.muted'>Configure the basic settings for this field.</Text>
                      </Box>
                      {component.type === 'sectionDivider' &&
                        <>
                          <FormControl isRequired label='Section title'>
                            <Input value={componentTitle ?? ''} onChange={e => setComponentTitle(e.target.value)} />
                          </FormControl>

                          <FormControl label='Section description'>
                            <Box w='100%'>
                              <RichText
                                value={componentDescription ?? ''} onChange={e => {
                                  setComponentDescription(e)
                                  if (descriptionBaselineRef.current === null) {
                                    descriptionBaselineRef.current = e
                                  } else {
                                    setChangesMade(true)
                                  }
                                }} forType='trial' forId={trial?._id} allowImages
                              />
                            </Box>
                          </FormControl>
                          {VideoElement}
                        </>}
                      {component.type === 'title' &&
                        <>
                          <FormControl isRequired label='Title text'>
                            <Input value={componentTitle ?? ''} onChange={e => setComponentTitle(e.target.value)} />
                          </FormControl>
                          {VideoElement}
                        </>}
                      {component.type === 'scheduledNotification' &&
                        <>
                          <Alert status='info' variant='left-accent' rounded='md' shadow='md'>Scheduled notifications can remind participants to do something after a period of time.</Alert>
                          <FormControl isRequired label='Guidance title'>
                            <Input value={componentTitle ?? ''} onChange={e => setComponentTitle(e.target.value)} />
                          </FormControl>

                          <FormControl label='Guidance description'>
                            <Box w='100%'>
                              <RichText value={componentDescription ?? ''} onChange={e => { setComponentDescription(e); if (descriptionBaselineRef.current === null) { descriptionBaselineRef.current = e } else { setChangesMade(true) } }} forType='trial' forId={trial?._id} allowImages />
                            </Box>
                          </FormControl>
                          {VideoElement}
                          <FormControl isRequired label='Notification title'>
                            <Input value={componentNotificationTitle ?? ''} onChange={e => setComponentNotificationTitle(e.target.value)} />
                          </FormControl>
                          <FormControl label='Notification body'>
                            <Input value={componentNotificationBody ?? ''} onChange={e => setComponentNotificationBody(e.target.value)} />
                          </FormControl>
                          <FormControl label='Notification delay (in seconds)'>
                            <Input value={componentNotificationDelay ?? ''} onChange={e => setComponentNotificationDelay(e.target.value)} />
                          </FormControl>
                        </>}

                      {!component.type &&
                        <>
                          <FormControl>
                            <Checkbox isChecked={componentIsRequired} onChange={e => setComponentIsRequired(e.checked)}>Make this a required field</Checkbox>
                          </FormControl>

                          <FormControl isRequired label='Question title'>
                            <Input value={componentTitle ?? ''} onChange={e => setComponentTitle(e.target.value)} />
                          </FormControl>

                          <FormControl label='Question description or instructions'>
                            <Box w='100%'>
                              <RichText value={componentDescription ?? ''} onChange={e => { setComponentDescription(e); if (descriptionBaselineRef.current === null) { descriptionBaselineRef.current = e } else { setChangesMade(true) } }} forType='trial' forId={trial?._id} allowImages />
                            </Box>
                          </FormControl>
                          {VideoElement}

                          <Box mt={3}>
                            <Heading size='md'>Data type (variable) settings</Heading>
                            <Text fontSize='sm' color='fg.muted'>Choose the variable this field collects. Please note that changing this type's settings will affect other stages also using this same data type.</Text>
                          </Box>

                          <FormControl isRequired label='Data type (variable)'>
                            <Stack direction='row' w='100%' alignItems='center'>
                              <Box flex={1}>
                                <SearchableSelect
                                  options={dataTypes?.map(d => ({ value: d._id, label: `${d.name} (${d.type})` })) ?? []}
                                  value={componentDataType ?? ''}
                                  onChange={setComponentDataType}
                                  placeholder='Select a data type'
                                  w='100%'
                                />
                              </Box>
                              <DataTypeEditor
                                trial={trial}
                                onComplete={dataType => setComponentDataType(dataType._id)}
                                trigger={
                                  <Button size='sm' variant='outline' colorPalette='primary' whiteSpace='nowrap'><Icon as={FaPlus} /> New data type (variable)</Button>
                                }
                              />
                            </Stack>
                          </FormControl>

                          {dataTypeError &&
                            <Alert status='error' variant='left-accent' title='There was a problem saving this data type'>
                              {dataTypeError}. Please fix these issues and try again.
                            </Alert>}

                          <DataTypeConfiguration
                            config={trackedDataTypeConfig}
                            selectedType={componentDataTypeObject?.type}
                            dataType={componentDataTypeObject}
                            isEditing
                            saveDataType={saveDataTypeSettings}
                          />
                        </>}
                    </Stack>)
                },
                supportsConditions(component) && {
                  key: 'conditional',
                  title: 'Conditions',
                  content: (
                    <Stack gap={4}>
                      <Box>
                        <Heading size='md'>Conditional display</Heading>
                        <Text fontSize='sm' color='fg.muted'>Set-up branching logic to show or hide this {componentNoun} based on answers to other fields.</Text>
                      </Box>
                      <Checkbox isChecked={conditional} onChange={e => setConditional(e.checked)}>Conditionally show this {componentNoun}</Checkbox>
                      {component.type === 'sectionDivider' &&
                        <Alert status='info' variant='left-accent' fontSize='sm'>
                          Hiding a section divider hides the divider itself, not the fields beneath it. Set the same condition on those fields to hide the whole section.
                        </Alert>}
                      {component.type === 'scheduledNotification' &&
                        <Alert status='info' variant='left-accent' fontSize='sm'>
                          A hidden notification is never offered, so the participant cannot set its timer. One they set before the condition stopped being met still arrives.
                        </Alert>}
                      {conditional &&
                        <Stack direction='column' alignItems='center'>
                          <Text fontSize='sm'>Show if</Text>
                          <SearchableSelect
                            options={dataTypes?.filter(d => d._id !== component.dataType).map(d => ({ value: d._id, label: `${d.name} (${d.type})` })) ?? []}
                            value={conditionDataType ?? ''}
                            onChange={value => {
                              const obj = dataTypes?.find(d => d._id === value)
                              setConditionOperator('')
                              setConditionValue(obj?.type === 'choice' ? [] : '')
                              setConditionStatement('')
                              setConditionDataType(value)
                            }}
                            placeholder='Choose a data type...'
                            size='sm'
                            emptyMessage='No other data types'
                          />
                          {conditionDataTypeObject?.type === 'likert' &&
                            <Select
                              bg='bg.surface' maxWidth={220} size='sm' value={conditionStatement}
                              onChange={e => setConditionStatement(e.target.value)}
                            >
                              <option value=''>total across statements</option>
                              {conditionDataTypeObject?.statements?.map(statement =>
                                <option key={statement.key} value={statement.key}>{statement.label}</option>
                              )}
                            </Select>}
                          <Select bg='bg.surface' maxWidth={150} size='sm' value={conditionOperator} onChange={e => setConditionOperator(e.target.value)} placeholder='Choose a condition...'>
                            <option key='exists' value='exists'>exists</option>
                            {(['integer', 'slider', 'rating', 'likert', 'float', 'height', 'weight', 'temperature', 'bmi', 'text', 'paragraph', 'choice'].indexOf(conditionDataTypeObject?.type) > -1) &&
                              <>
                                <option key='equal' value='equal'>is equal to</option>
                                <option key='notequal' value='notequal'>is not equal to</option>
                                {(['integer', 'slider', 'rating', 'likert', 'float', 'height', 'weight', 'temperature', 'bmi'].indexOf(conditionDataTypeObject?.type) > -1) &&
                                  <>
                                    <option key='more' value='more'>is more than</option>
                                    <option key='less' value='less'>is less than</option>
                                  </>}
                              </>}
                          </Select>
                          {['equal', 'notequal', 'more', 'less'].indexOf(conditionOperator) > -1 &&
                            <>
                              {conditionDataTypeObject?.type === 'choice' &&
                                <Stack alignItems='center'>
                                  <Text fontSize='sm'>One of</Text>
                                  <Wrap gap={2}>
                                    {conditionDataTypeObject?.options?.map(option => {
                                      const isSelected = conditionValue?.indexOf(option) > -1
                                      return (
                                        <WrapItem key={option}>
                                          <Tag
                                            cursor='pointer'
                                            colorPalette={isSelected ? 'primary' : 'gray'}
                                            onClick={e => {
                                              const index = conditionValue?.indexOf(option)
                                              const newCV = Object.assign([], conditionValue)
                                              if (index === -1) newCV.push(option)
                                              else newCV.splice(index, 1)
                                              setConditionValue(newCV)
                                            }}
                                          >{isSelected ? <Icon as={FaCheck} /> : <Icon as={FaPlus} />} {option}
                                          </Tag>
                                        </WrapItem>
                                      )
                                    })}
                                  </Wrap>
                                </Stack>}
                              {['integer', 'float', 'height', 'weight', 'temperature', 'bmi', 'text', 'paragraph', 'slider', 'rating', 'likert'].indexOf(conditionDataTypeObject?.type) > -1 &&
                                <Input
                                  bg='bg.surface' ml={1} maxWidth={200} size='sm' placeholder={conditionDataTypeObject?.type === 'rating' ? `Point, 1 to ${ratingPoints(conditionDataTypeObject)}` : 'Enter a value...'} value={conditionValue ?? ''} onChange={e => {
                                    const val = e.target.value
                                    if (['text', 'paragraph'].indexOf(conditionDataTypeObject.type) > -1) { setConditionValue(val) } else if (['integer', 'slider', 'rating', 'likert'].indexOf(conditionDataTypeObject.type) > -1) { setConditionValue(val === '' ? '' : val.replace(/[^\d-]/g, '')) } else if (['float', 'height', 'weight', 'temperature', 'bmi'].indexOf(conditionDataTypeObject.type) > -1) { setConditionValue(val === '' ? '' : val.replace(/[^\d.]/g, '')) }
                                  }}
                                />}
                            </>}
                        </Stack>}
                    </Stack>)
                },
                !component.type && {
                  key: 'scoring',
                  title: 'Scoring',
                  content: (
                    <Stack gap={4}>
                      <Box>
                        <Heading size='md'>Scoring rules</Heading>
                        <Text fontSize='sm' color='fg.muted'>Set-up rules to assign a score to this field based on the answers to other fields.</Text>
                        {componentDataTypeObject?.type === 'calculation' &&
                          <Text fontSize='sm' color='fg.muted'>Note that scores for calculation data types assume the calculation output is a <strong>number</strong>.</Text>}
                        {componentDataTypeObject?.type === 'promisForm' &&
                          <Text fontSize='sm' color='fg.muted'>Scores for PROMIS forms are based on the form's <strong>T score</strong>. A form that wasn't scored counts as not existing.</Text>}
                        {componentDataTypeObject?.type === 'promisBattery' &&
                          <Text fontSize='sm' color='fg.muted'>Scores for PROMIS batteries are based on the battery's <strong>PROPr score</strong>. A battery that isn't PROPr-scorable counts as not existing.</Text>}
                      </Box>
                      {scoringRules?.map((rule, i) =>
                        <Stack p={2} key={rule.id} borderWidth='1px' rounded='md' bg='bg.muted' direction='row' alignItems='center' gap={2}>
                          <Text>If</Text>
                          <Select bg='bg.surface' size='sm' value={rule.operator} onChange={e => setScoringRules(scoringRules.map(r => r.id === rule.id ? { ...r, operator: e.target.value } : r))} placeholder='Choose a condition...'>
                            <option key='exists' value='exists'>exists</option>
                            <option key='notexists' value='notexists'>does not exist</option>
                            {(['choice', 'integer', 'slider', 'rating', 'likert', 'float', 'height', 'weight', 'temperature', 'bmi', 'date', 'datetime', 'time', 'calculation', 'promisForm', 'promisBattery'].indexOf(componentDataTypeObject?.type) > -1) &&
                              <>
                                <option key='equal' value='equal'>is equal to</option>
                                <option key='notequal' value='notequal'>is not equal to</option>
                                {(['integer', 'slider', 'rating', 'likert', 'float', 'height', 'weight', 'temperature', 'bmi', 'date', 'datetime', 'time', 'calculation', 'promisForm', 'promisBattery'].indexOf(componentDataTypeObject?.type) > -1) &&
                                  <>
                                    <option key='more' value='more'>is more than</option>
                                    <option key='less' value='less'>is less than</option>
                                  </>}
                              </>}
                          </Select>
                          {['equal', 'notequal', 'more', 'less'].indexOf(rule.operator) > -1 &&
                            <>
                              {['integer', 'slider', 'rating', 'likert', 'float', 'height', 'weight', 'temperature', 'bmi', 'calculation', 'promisForm', 'promisBattery'].indexOf(componentDataTypeObject?.type) > -1 &&
                                <Input bg='bg.surface' maxWidth={150} size='sm' placeholder={componentDataTypeObject?.type === 'promisForm' ? 'e.g. 50' : componentDataTypeObject?.type === 'promisBattery' ? 'e.g. 0.5' : componentDataTypeObject?.type === 'likert' ? 'Total across statements, e.g. 12' : componentDataTypeObject?.type === 'rating' ? `Point, 1 to ${ratingPoints(componentDataTypeObject)}` : 'Enter a value...'} value={rule.value ?? ''} onChange={e => setScoringRules(scoringRules.map(r => r.id === rule.id ? { ...r, value: e.target.value } : r))} />}
                              {['choice'].indexOf(componentDataTypeObject?.type) > -1 &&
                                <Select bg='bg.surface' size='sm' placeholder='Choose a value...' value={rule.value ?? ''} onChange={e => setScoringRules(scoringRules.map(r => r.id === rule.id ? { ...r, value: e.target.value } : r))}>
                                  {componentDataTypeObject?.options?.map(option =>
                                    <option key={option} value={option}>{option}</option>
                                  )}
                                </Select>}
                              {['date', 'datetime'].indexOf(componentDataTypeObject?.type) > -1 &&
                                <DateChooser size='sm' value={rule.value ?? ''} onChange={e => setScoringRules(scoringRules.map(r => r.id === rule.id ? { ...r, value: e } : r))} withTime={componentDataTypeObject?.type === 'datetime'} />}
                              {componentDataTypeObject?.type === 'time' &&
                                <TimeChooser size='sm' value={rule.value ?? ''} onChange={e => setScoringRules(scoringRules.map(r => r.id === rule.id ? { ...r, value: e } : r))} />}
                            </>}
                          <Text>Add</Text>
                          <Input
                            bg='bg.surface' maxWidth={100} size='sm' type='number' value={rule.points} onChange={e => setScoringRules(scoringRules.map(r => r.id === rule.id ? { ...r, points: e.target.value } : r))} placeholder='1'
                          />
                          <Button size='xs' colorPalette='gray' variant='ghost' onClick={() => setScoringRules(scoringRules.filter(r => r.id !== rule.id))}><Icon as={FaTrash} /></Button>
                        </Stack>
                      )}
                      <Button size='sm' variant='outline' colorPalette='primary' onClick={() => setScoringRules([...scoringRules, { id: utils.uuidv4(), operator: 'exists', value: '', points: 0 }])}>
                        <Icon><FaPlus /></Icon> Add a scoring rule
                      </Button>
                    </Stack>)
                }
              ]}
            />

            <Box mt={10}>
              <BottomStickyBar justifyContent='end'>
                <Button variant='ghost' onClick={checkOnClose}>Discard changes</Button>
                <Button colorPalette='primary' onClick={keepChanges} isLoading={savingDataType}>Save</Button>
              </BottomStickyBar>
            </Box>
          </Box>
              }
      />
    </>
  )
}
